"use client";

import * as React from "react";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";
import {cn} from "@/lib/utils";

export interface ScrollableViewportProps extends React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> {
  widthClass?: string;
  heightClass?: string;
}

export const ScrollableViewport = React.forwardRef<
    React.ElementRef<typeof ScrollAreaPrimitive.Root>,
    ScrollableViewportProps
>(({ className, widthClass = 'w-full', heightClass = 'h-full', children, ...props }, ref) => {
  const viewportRef = React.useRef<HTMLDivElement>(null);
  const isPressed = React.useRef(false);
  const isDragging = React.useRef(false);
  const startPos = React.useRef({ x: 0, y: 0 });
  const scrollStart = React.useRef({ left: 0, top: 0 });

  const onPointerDown = (e: React.PointerEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button, a, [role="button"], input, select, textarea')) return;
    isPressed.current = true;
    startPos.current = { x: e.clientX, y: e.clientY };
    if (viewportRef.current) {
      scrollStart.current = {
        left: viewportRef.current.scrollLeft,
        top: viewportRef.current.scrollTop
      };
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!isPressed.current || !viewportRef.current) return;
    const dx = e.clientX - startPos.current.x;
    const dy = e.clientY - startPos.current.y;
    if (!isDragging.current) {
      if (Math.hypot(dx, dy) < 5) return;
      isDragging.current = true;
      viewportRef.current.setPointerCapture(e.pointerId);
    }
    viewportRef.current.scrollTo({
      left: scrollStart.current.left - dx,
      top: scrollStart.current.top - dy
    });
  };

  const onPointerUp = (e: React.PointerEvent) => {
    if (isDragging.current && viewportRef.current) {
      viewportRef.current.releasePointerCapture(e.pointerId);
    }
    isPressed.current = false;
    isDragging.current = false;
  };

  return (
      <ScrollAreaPrimitive.Root
          ref={ref}
          className={cn('relative overflow-hidden', widthClass, heightClass, className)}
          {...props}
      >
        <ScrollAreaPrimitive.Viewport
            ref={viewportRef}
            className={cn(
                'w-full h-full overflow-auto rounded-[inherit]',
                isDragging.current ? 'cursor-grabbing' : 'cursor-grab'
            )}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
        >
          {children}
        </ScrollAreaPrimitive.Viewport>

        <ScrollAreaPrimitive.ScrollAreaScrollbar
            orientation="vertical"
            className="flex touch-none select-none transition-colors h-full w-2.5 border-l border-l-transparent p-[1px]"
        >
          <ScrollAreaPrimitive.ScrollAreaThumb className="relative flex-1 rounded-full bg-border" />
        </ScrollAreaPrimitive.ScrollAreaScrollbar>

        <ScrollAreaPrimitive.ScrollAreaScrollbar
            orientation="horizontal"
            className="flex touch-none select-none transition-colors w-full h-2.5 border-t border-t-transparent p-[1px]"
        >
          <ScrollAreaPrimitive.ScrollAreaThumb className="relative flex-1 rounded-full bg-border" />
        </ScrollAreaPrimitive.ScrollAreaScrollbar>

        <ScrollAreaPrimitive.Corner />
      </ScrollAreaPrimitive.Root>
  );
});
ScrollableViewport.displayName = 'ScrollableViewport';
